import React, { useState } from 'react';
import { RotateCw } from 'lucide-react';
import { portfolioData } from '../mock/data';

const Portfolio = () => {
  const [flippedCards, setFlippedCards] = useState([]);

  const toggleFlip = (cardId) => {
    setFlippedCards(prev => 
      prev.includes(cardId) 
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#033530] mb-4">
            Áreas de Atuação
          </h2>
          <p className="text-lg text-[#033530]/70">
            Conheça nosso portfólio de serviços e projetos realizados com excelência técnica
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {portfolioData.map((item) => (
            <div 
              key={item.id} 
              className="flip-card h-[400px]"
              onClick={() => toggleFlip(item.id)}
            >
              <div className={`flip-card-inner ${flippedCards.includes(item.id) ? 'flipped' : ''}`}>
                {/* Front of card */}
                <div className="flip-card-front rounded-2xl shadow-lg overflow-hidden bg-[#e8d6b2]">
                  {/* Image Container */}
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-[#033530]/70 z-10" />
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Icon Overlay */}
                    <div className="absolute top-4 right-4 z-20 w-12 h-12 bg-[#ddc183] rounded-full flex items-center justify-center text-2xl">
                      {item.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-[#033530] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-[#033530]/70 text-xl leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  {/* Rotate Icon */}
                  <div className="absolute bottom-4 right-4">
                    <div className="w-8 h-8 bg-[#033530] rounded-full flex items-center justify-center">
                      <RotateCw size={18} className="text-[#ddc183]" />
                    </div>
                  </div>

                  {/* Bottom Border Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#ddc183]" />
                </div>

                {/* Back of card */}
                <div className="flip-card-back rounded-2xl shadow-lg overflow-hidden bg-gradient-to-br from-[#033530] to-[#044a44] p-6 flex flex-col">
                  {/* Icon and Title */}
                  <div className="text-center mb-4">
                    <div className="text-4xl mb-2">{item.icon}</div>
                    <h3 className="text-2xl font-bold text-[#ddc183] whitespace-pre-line leading-tight">
                      {item.backTitle}
                    </h3>
                  </div>

                  {/* Content List */}
                  <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <ul className="space-y-2 text-[#e8d6b2] text-xl">
                      {item.backContent.map((content, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-[#ddc183] mt-1 flex-shrink-0">•</span>
                          <span className="leading-snug">{content}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Bottom accent */}
                  <div className="mt-4 pt-3 border-t border-[#ddc183]/30 flex justify-center">
                    <div className="w-8 h-8 bg-[#ddc183] rounded-full flex items-center justify-center">
                      <RotateCw size={18} className="text-[#033530]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-[#033530] rounded-2xl p-10 max-w-4xl mx-auto shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold text-[#e8d6b2] mb-4">
              Precisa de um serviço específico?
            </h3>
            <p className="text-[#e8d6b2]/80 mb-6 text-lg">
              Entre em contato conosco para discutir suas necessidades técnicas
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contato');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="bg-[#ddc183] text-[#033530] px-8 py-4 rounded-lg hover:bg-[#c9af71] transition-colors duration-300 font-bold text-base shadow-lg hover:scale-105 transform transition-transform"
            >
              Solicitar Consulta
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .flip-card {
          perspective: 1000px;
          cursor: pointer;
        }

        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.6s;
          transform-style: preserve-3d;
        }

        .flip-card-inner.flipped {
          transform: rotateY(180deg);
        }

        @media (hover: hover) {
          .flip-card:hover .flip-card-inner:not(.flipped) {
            transform: rotateY(180deg);
          }
        }

        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .flip-card-back {
          transform: rotateY(180deg);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(221, 193, 131, 0.1);
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(221, 193, 131, 0.5);
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(221, 193, 131, 0.7);
        }
      `}</style>
    </section>
  );
};

export default Portfolio;
